import get from 'lodash/get';
import T from 'prop-types';
import React from 'react';
import * as builtinCustomComponents from './CustomComponents';
import { DynamicFormErrorBoundary } from './DynamicFormErrorBoundary';
import { DynamicFormField } from './DynamicFormField';
import { DynamicFormInput, getInputMetadata } from './DynamicFormInput';
import { DF_KEYWORD_CUSTOM_FIELD, DF_KEYWORD_CUSTOM_INPUT } from './constants';
import {
  DetailedError,
  formNameFromKeyPath,
  getCustomComponentDefinition,
  isReadOnlyValue,
  requiredArg,
} from './utils';

export const ErrorCustomFieldComponentNotFound = DetailedError(
  'ErrorCustomFieldComponentNotFound',
  'A custom field component was not found in customComponents registry'
);

export const ErrorCustomComponentPreviouslyRendered = DetailedError(
  'ErrorCustomComponentPreviouslyRendered',
  `This field component was already rendered once in this tree.
  If the component is recursive it should remove itself from
  context.customComponentsInPath before calling renderChildren.`
);

export const ErrorCustomComponentNotAvailable = DetailedError(
  'ErrorCustomComponentNotAvailable',
  'Custom Components are defined, but there is no custom component available to render.'
);

export const DynamicFormCustomComponent = ({
  formValues = requiredArg('formValues'),
  schemaNode = requiredArg('schemaNode'),
  context = requiredArg('context'),
  customComponents = requiredArg('customComponents'),
  getComponentProps = requiredArg('getComponentProps'),
  setMetadataForFieldName = requiredArg('setMetadataForFieldName'),
  getMetadataForFieldName = requiredArg('getMetadataForFieldName'),
  getPageLevelErrors = requiredArg('getPageLevelErrors'),
  updateFormStructureForPath = requiredArg('updateFormStructureForPath'),
  renderChildren,
}) => {
  const customComponentsInPath = get(context, 'customComponentsInPath', []);

  const customFieldComponent = getCustomComponentDefinition(
    schemaNode,
    DF_KEYWORD_CUSTOM_FIELD
  );

  const customInputComponent = getCustomComponentDefinition(
    schemaNode,
    DF_KEYWORD_CUSTOM_INPUT
  );

  const customComponent = [
    customComponentsInPath.includes(
      get(customFieldComponent, 'customComponentName')
    )
      ? null
      : customFieldComponent,
    customInputComponent,
  ].find(Boolean);

  if (
    customComponentsInPath.includes(
      get(customFieldComponent, 'customComponentName')
    ) &&
    !customComponent
  )
    throw new ErrorCustomComponentPreviouslyRendered();

  if (!customComponent) throw new ErrorCustomComponentNotAvailable();

  const { customComponentName, customComponentProps, customComponentType } =
    customComponent;

  if (customComponentsInPath.includes(customComponentName))
    throw new ErrorCustomComponentPreviouslyRendered();

  if (customComponentType === DF_KEYWORD_CUSTOM_FIELD) {
    const CustomComponent =
      customComponents[customComponentName] ||
      builtinCustomComponents[customComponentName];

    if (!CustomComponent)
      throw new ErrorCustomFieldComponentNotFound(
        `No registered component found for "${customComponentName}"`
      );

    const customComponentContext = {
      ...context,
      parent: `CustomComponent:${customComponentName}`,
      customComponentsInPath: [...customComponentsInPath, customComponentName],
    };

    const defaultProps = getComponentProps({
      context: customComponentContext,
      formValues,
      schemaNode,
    });

    return (
      <DynamicFormErrorBoundary
        detail={JSON.stringify(customComponentContext, null, 2)}
        title={`Error rendering ${customComponentName}`}
      >
        <CustomComponent
          getPageLevelErrors={getPageLevelErrors}
          key={get(context, 'schemaPath', []).join('-') + customComponentName}
          setMetadataForFieldName={setMetadataForFieldName}
          {...customComponentProps}
          {...(!CustomComponent.isWrapper
            ? {
                ...defaultProps,
                updateFormStructureForPath,
              }
            : {})}
        >
          {CustomComponent.isWrapper ? renderChildren(defaultProps) : null}
        </CustomComponent>
      </DynamicFormErrorBoundary>
    );
  }

  if (customComponentType === DF_KEYWORD_CUSTOM_INPUT) {
    const componentProps = getComponentProps({
      context: { ...context, parent: 'DynamicFormField' },
      formValues,
      schemaNode,
    });

    const { componentName } = getInputMetadata({
      context,
      schemaNode,
    });

    const CustomComponent =
      customComponents[componentName] || builtinCustomComponents[componentName];

    const componentFormStructure = get(CustomComponent, 'formStructure', null);
    const { keyPath = requiredArg('context.keyPath') } = context;

    const readOnly = isReadOnlyValue(
      formValues.readOnly,
      formNameFromKeyPath(keyPath)
    );

    return (
      <DynamicFormErrorBoundary>
        <DynamicFormField
          {...{
            ...componentProps,
            setMetadataForFieldName,
            getMetadataForFieldName,
            getPageLevelErrors,
            readOnly,
            ...(componentFormStructure
              ? {
                  doUpdateFormStructureForPath: () => {
                    updateFormStructureForPath(
                      context.keyPath,
                      componentFormStructure
                    );
                  },
                }
              : {}),
            renderChildren() {
              return (
                <DynamicFormErrorBoundary>
                  <DynamicFormInput
                    {...componentProps}
                    componentOverride={{
                      customComponentName,
                      customComponentProps,
                    }}
                    getMetadataForFieldName={getMetadataForFieldName}
                    readOnly={readOnly}
                    renderChildren={renderChildren}
                    setMetadataForFieldName={setMetadataForFieldName}
                  />
                </DynamicFormErrorBoundary>
              );
            },
          }}
        />
      </DynamicFormErrorBoundary>
    );
  }
};

DynamicFormCustomComponent.propTypes = {
  context: T.shape({
    keyPath: T.object,
    schemaPath: T.arrayOf(T.oneOfType([T.string, T.number])),
  }),
  formValues: T.object,
  schemaNode: T.object,
  customComponents: T.object,
  getComponentProps: T.func,
  setMetadataForFieldName: T.func,
  getMetadataForFieldName: T.func,
  getPageLevelErrors: T.func,
  updateFormStructureForPath: T.func,
  renderChildren: T.func,
};
